import AddIcon from "@mui/icons-material/Add";
import FolderIcon from "@mui/icons-material/Folder";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  Menu,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { reportBackendError } from "@shared/api/client";
import type { PowerPlant, SpofRegisterImport } from "@shared/types/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import {
  createPowerPlant,
  deletePowerPlant,
  exportAssetMappingByPlant,
  exportRequiredEvidencePackByPlant,
  exportTotalEvidencePackByPlant,
  getPowerPlants,
  type PowerPlantBody,
  updatePowerPlant,
  uploadFunctionalLocations,
} from "../api";
import { ConditionalMenuItem } from "./ConditionalMenuItem";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import { LockStatusIcon } from "./LockStatusIcon";
import SpofRegisterImportDialog from "./SpofRegisterImportDialog";
import { StatusChipFromSummary } from "./StatusChip";

interface Props {
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  isAdmin: boolean;
}

/** Static plant facts as typed in the dialog; empty means "no value stored". */
interface StaticDataForm {
  name: string;
  latitude: string;
  longitude: string;
  configuration: string;
  technology: string;
}

const EMPTY_FORM: StaticDataForm = {
  name: "",
  latitude: "",
  longitude: "",
  configuration: "",
  technology: "",
};

function formOf(plant: PowerPlant): StaticDataForm {
  return {
    name: plant.name,
    latitude: plant.latitude?.toString() ?? "",
    longitude: plant.longitude?.toString() ?? "",
    configuration: plant.configuration ?? "",
    technology: plant.technology ?? "",
  };
}

function bodyOf(form: StaticDataForm): PowerPlantBody {
  const number = (value: string) =>
    value.trim() === "" ? null : Number(value.trim());
  return {
    name: form.name.trim(),
    latitude: number(form.latitude),
    longitude: number(form.longitude),
    configuration: form.configuration.trim() || null,
    technology: form.technology.trim() || null,
  };
}

/** Rejects what the backend would reject, so the dialog reports it before saving. */
function coordinateError(value: string, bound: number): boolean {
  if (value.trim() === "") return false;
  const parsed = Number(value.trim());
  return Number.isNaN(parsed) || Math.abs(parsed) > bound;
}

export default function PowerPlantList({
  selectedId,
  onSelect,
  isAdmin,
}: Props) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [editDialog, setEditDialog] = useState<{
    open: boolean;
    id: string | null;
    form: StaticDataForm;
  }>({ open: false, id: null, form: EMPTY_FORM });
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const spofInputRef = useRef<HTMLInputElement>(null);
  const spofPlantId = useRef<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | {
    id: string;
    el: HTMLElement;
  }>(null);
  const [exportSnackbarOpen, setExportSnackbarOpen] = useState(false);
  const [uploadSnackbarOpen, setUploadSnackbarOpen] = useState(false);
  const [spofResult, setSpofResult] = useState<SpofRegisterImport | null>(null);
  // The workbook is only imported once its proposed KKS links are approved in the dialog.
  const [spofUpload, setSpofUpload] = useState<{
    id: string;
    file: File;
  } | null>(null);
  const [duplicateLocations, setDuplicateLocations] = useState<string[] | null>(
    null,
  );

  const handleExportPlant = async (id: string) => {
    try {
      await exportAssetMappingByPlant(id);
      setExportSnackbarOpen(true);
    } catch (e) {
      reportBackendError(e instanceof Error ? e.message : "Export failed");
    }
  };

  const handleExportEvidencePack = async (id: string) => {
    try {
      await exportTotalEvidencePackByPlant(id);
      setExportSnackbarOpen(true);
    } catch (e) {
      reportBackendError(e instanceof Error ? e.message : "Export failed");
    }
  };

  const handleExportRequiredEvidencePack = async (id: string) => {
    try {
      await exportRequiredEvidencePackByPlant(id);
      setExportSnackbarOpen(true);
    } catch (e) {
      reportBackendError(e instanceof Error ? e.message : "Export failed");
    }
  };

  const {
    data: powerPlants = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["powerPlants"],
    queryFn: getPowerPlants,
    refetchInterval: 30_000,
  });

  const createMut = useMutation({
    mutationFn: (name: string) => createPowerPlant(name),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["powerPlants"] });
      setAdding(false);
      setNewName("");
      onSelect(created.id);
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, ...body }: PowerPlantBody & { id: string }) =>
      updatePowerPlant(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["powerPlants"] });
      setEditDialog((d) => ({ ...d, open: false }));
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deletePowerPlant(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["powerPlants"] });
      if (selectedId === deleteId) onSelect(null);
      setDeleteId(null);
    },
  });

  const uploadFuncLocMut = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      uploadFunctionalLocations(id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["powerPlants"] });
      setUploadSnackbarOpen(true);
    },
    onError: (error: unknown) => {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        const detail = (
          error.response.data as { detail?: { duplicates?: string[] } }
        )?.detail;
        if (detail?.duplicates && detail.duplicates.length > 0) {
          setDuplicateLocations(detail.duplicates);
        }
      }
    },
  });

  const deleteName = powerPlants.find((p) => p.id === deleteId)?.name;
  const menuPlant = powerPlants.find((pp) => pp.id === menuAnchor?.id);
  const editPlant = powerPlants.find((pp) => pp.id === editDialog.id);
  const latitudeError = coordinateError(editDialog.form.latitude, 90);
  const longitudeError = coordinateError(editDialog.form.longitude, 180);

  useEffect(() => {
    if (!menuAnchor) return;
    const handler = (e: MouseEvent) => {
      e.preventDefault();
      const menu = document.querySelector('[role="menu"]');
      if (menu?.contains(e.target as Node)) return;
      const elements = document.elementsFromPoint(e.clientX, e.clientY);
      const ppEl = elements.find(
        (el) => el instanceof HTMLElement && (el as HTMLElement).dataset.ppId,
      ) as HTMLElement | undefined;
      if (ppEl?.dataset.ppId) {
        setMenuAnchor({ id: ppEl.dataset.ppId, el: ppEl });
      } else {
        setMenuAnchor(null);
      }
    };
    document.addEventListener("contextmenu", handler, true);
    return () => document.removeEventListener("contextmenu", handler, true);
  }, [menuAnchor]);

  return (
    <Box>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ color: "#002A5C", fontSize: "0.85rem", fontWeight: 700 }}
        >
          Power Plants
        </Typography>
        {isAdmin && (
          <Tooltip title="Add power plant" placement="right">
            <IconButton
              size="small"
              onClick={() => setAdding(true)}
              sx={{
                color: "#8E99A8",
                "&:hover": {
                  color: "#009DE0",
                  bgcolor: "rgba(0,157,224,0.08)",
                },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {adding && (
        <Box
          component="form"
          sx={{
            px: 1.5,
            pb: 1,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
          onSubmit={(e) => {
            e.preventDefault();
            if (newName.trim()) createMut.mutate(newName.trim());
          }}
        >
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Plant name…"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setAdding(false);
                setNewName("");
              }
            }}
          />
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Button
              type="submit"
              variant="contained"
              size="small"
              disabled={!newName.trim() || createMut.isPending}
              sx={{
                flex: 1,
                bgcolor: "#009DE0",
                "&:hover": { bgcolor: "#0087C3" },
                textTransform: "none",
                fontSize: "0.75rem",
              }}
            >
              Add
            </Button>
            <Button
              size="small"
              onClick={() => {
                setAdding(false);
                setNewName("");
              }}
              sx={{
                flex: 1,
                color: "#5a6a9a",
                textTransform: "none",
                fontSize: "0.75rem",
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      {isLoading && (
        <Typography variant="caption" sx={{ px: 2, color: "#999" }}>
          Loading…
        </Typography>
      )}
      {isError && (
        <Typography variant="caption" sx={{ px: 2, color: "#ef5350" }}>
          Failed to load.
        </Typography>
      )}

      <List dense disablePadding>
        {powerPlants.map((pp) => (
          <ListItemButton
            key={pp.id}
            data-pp-id={pp.id}
            selected={pp.id === selectedId}
            onClick={() => onSelect(pp.id)}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setMenuAnchor({ id: pp.id, el: e.currentTarget });
            }}
            sx={{
              px: 2,
              py: 0.75,
              minHeight: 45,
              mx: 0.5,
              borderRadius: 1.5,
              mb: 0.25,
              color: pp.id === selectedId ? "#009DE0" : "#444",
              bgcolor:
                pp.id === selectedId ? "rgba(0,157,224,0.1)" : "transparent",
              "&:hover": {
                bgcolor:
                  pp.id === selectedId ? "rgba(0,157,224,0.15)" : "#EDF1F7",
                color: pp.id === selectedId ? "#009DE0" : "#002A5C",
              },
              "&.Mui-selected": { bgcolor: "rgba(0,157,224,0.1)" },
              "&.Mui-selected:hover": { bgcolor: "rgba(0,157,224,0.15)" },
            }}
          >
            <FolderIcon
              sx={{ fontSize: 18, color: "#8E99A8", mr: 1, flexShrink: 0 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "0.82rem",
                  fontWeight: pp.id === selectedId ? 600 : 400,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: pp.id === selectedId ? "#009DE0" : "inherit",
                }}
              >
                {pp.name}
              </Typography>
            </Box>
            {pp.status && (
              <StatusChipFromSummary
                status={pp.status}
                percentage={pp.status_percentage}
              />
            )}
            {pp.lock_status && <LockStatusIcon lockStatus={pp.lock_status} />}
            {!pp.functional_locations_uploaded_at && (
              <Tooltip
                title="Functional locations not uploaded"
                placement="top"
              >
                <WarningAmberIcon
                  sx={{
                    fontSize: 14,
                    color: "#FF9800",
                    ml: 0.5,
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            )}
            <Box sx={{ ml: 0.5 }} onClick={(e) => e.stopPropagation()}>
              <IconButton
                size="small"
                onClick={(e) =>
                  setMenuAnchor({ id: pp.id, el: e.currentTarget })
                }
                sx={{
                  color: "#8E99A8",
                  p: 0.25,
                  "&:hover": { color: "#009DE0" },
                }}
              >
                <MoreVertIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </ListItemButton>
        ))}
      </List>

      <Menu
        anchorEl={menuAnchor?.el}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        disableRestoreFocus
        slotProps={{ paper: { sx: { minWidth: 230 } } }}
      >
        <MenuItem
          dense
          sx={{ fontSize: "0.82rem" }}
          onClick={() => {
            if (menuAnchor) handleExportPlant(menuAnchor.id);
            setMenuAnchor(null);
          }}
        >
          Export Asset Mapping
        </MenuItem>
        <ConditionalMenuItem
          disabled={menuPlant?.required_spof_lock_status !== "locked"}
          disabledTitle="All PIDs must be required-SPOF-locked first"
          onClick={() => {
            if (menuAnchor) handleExportRequiredEvidencePack(menuAnchor.id);
            setMenuAnchor(null);
          }}
        >
          Export Evidence Pack
        </ConditionalMenuItem>
        <ConditionalMenuItem
          disabled={menuPlant?.lock_status !== "locked"}
          disabledTitle="All PIDs must be locked first"
          onClick={() => {
            if (menuAnchor) handleExportEvidencePack(menuAnchor.id);
            setMenuAnchor(null);
          }}
        >
          Export Total Evidence Pack
        </ConditionalMenuItem>
        <ConditionalMenuItem
          disabled={!isAdmin}
          disabledTitle="Admin only"
          onClick={() => {
            if (menuAnchor && menuPlant) {
              setEditDialog({
                open: true,
                id: menuPlant.id,
                form: formOf(menuPlant),
              });
            }
            setMenuAnchor(null);
          }}
        >
          Edit
        </ConditionalMenuItem>
        <ConditionalMenuItem
          disabled={!isAdmin}
          disabledTitle="Admin only"
          onClick={() => {
            spofPlantId.current = menuAnchor?.id ?? null;
            setMenuAnchor(null);
            spofInputRef.current?.click();
          }}
        >
          Upload SPOF Register
        </ConditionalMenuItem>
        <ConditionalMenuItem
          disabled={!isAdmin}
          disabledTitle="Admin only"
          color="#E30613"
          onClick={() => {
            if (menuAnchor) setDeleteId(menuAnchor.id);
            setMenuAnchor(null);
          }}
        >
          Delete
        </ConditionalMenuItem>
      </Menu>
      <ConfirmDeleteDialog
        open={deleteId !== null}
        title="Delete Power Plant"
        message={
          <>
            Are you sure you want to delete <strong>{deleteName}</strong>? This
            will also delete all its systems and uploaded PIDs.
          </>
        }
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && deleteMut.mutate(deleteId)}
        isPending={deleteMut.isPending}
      />

      {/* Edit power plant dialog */}
      <Dialog
        open={editDialog.open}
        onClose={() => setEditDialog((d) => ({ ...d, open: false }))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Power Plant</DialogTitle>
        <DialogContent>
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}
          >
            <TextField
              autoFocus
              label="Display name"
              size="small"
              fullWidth
              value={editDialog.form.name}
              onChange={(e) =>
                setEditDialog((d) => ({
                  ...d,
                  form: { ...d.form, name: e.target.value },
                }))
              }
            />
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontSize: "0.8rem", fontWeight: 700, mb: 1 }}
              >
                Static Data
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label="Latitude"
                    size="small"
                    fullWidth
                    value={editDialog.form.latitude}
                    error={latitudeError}
                    helperText={latitudeError ? "Between -90 and 90" : null}
                    onChange={(e) =>
                      setEditDialog((d) => ({
                        ...d,
                        form: { ...d.form, latitude: e.target.value },
                      }))
                    }
                  />
                  <TextField
                    label="Longitude"
                    size="small"
                    fullWidth
                    value={editDialog.form.longitude}
                    error={longitudeError}
                    helperText={longitudeError ? "Between -180 and 180" : null}
                    onChange={(e) =>
                      setEditDialog((d) => ({
                        ...d,
                        form: { ...d.form, longitude: e.target.value },
                      }))
                    }
                  />
                </Box>
                <TextField
                  label="Configuration"
                  size="small"
                  fullWidth
                  placeholder="3 blocks of 4 GTs"
                  value={editDialog.form.configuration}
                  onChange={(e) =>
                    setEditDialog((d) => ({
                      ...d,
                      form: { ...d.form, configuration: e.target.value },
                    }))
                  }
                />
                <TextField
                  label="Technology"
                  size="small"
                  fullWidth
                  placeholder="Combined Cycle"
                  value={editDialog.form.technology}
                  onChange={(e) =>
                    setEditDialog((d) => ({
                      ...d,
                      form: { ...d.form, technology: e.target.value },
                    }))
                  }
                />
                <Typography variant="caption" sx={{ color: "#5a6a9a" }}>
                  Clear a field and save to delete the stored value.
                </Typography>
              </Box>
            </Box>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontSize: "0.8rem", fontWeight: 700, mb: 0.5 }}
              >
                Functional Locations
              </Typography>
              {editPlant?.functional_locations_uploaded_at ? (
                <Typography variant="body2" sx={{ color: "#5a6a9a" }}>
                  {editPlant.functional_locations_count.toLocaleString()}{" "}
                  functional location
                  {editPlant.functional_locations_count === 1 ? "" : "s"}{" "}
                  uploaded on{" "}
                  {new Date(
                    editPlant.functional_locations_uploaded_at,
                  ).toLocaleString()}
                </Typography>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <WarningAmberIcon sx={{ fontSize: 16, color: "#FF9800" }} />
                  <Typography variant="body2" sx={{ color: "#FF9800" }}>
                    Functional locations not uploaded
                  </Typography>
                </Box>
              )}
              <Button
                size="small"
                sx={{ mt: 1, textTransform: "none" }}
                onClick={() => fileInputRef.current?.click()}
              >
                {editPlant?.functional_locations_uploaded_at
                  ? "Replace file (.xlsx)"
                  : "Upload file (.xlsx)"}
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog((d) => ({ ...d, open: false }))}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              !editDialog.form.name.trim() ||
              latitudeError ||
              longitudeError ||
              updateMut.isPending
            }
            onClick={() =>
              editDialog.id &&
              updateMut.mutate({
                id: editDialog.id,
                ...bodyOf(editDialog.form),
              })
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={exportSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setExportSnackbarOpen(false)}
        message="Export requested — you will receive an email when it is ready."
      />
      <Snackbar
        open={uploadSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setUploadSnackbarOpen(false)}
        message="Functional locations uploaded successfully."
      />
      <Dialog
        open={duplicateLocations !== null}
        onClose={() => setDuplicateLocations(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Duplicate Functional Locations</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            The following functional locations appear more than once in the
            uploaded file. Please fix the duplicates and re-upload.
          </Typography>
          <Box
            component="ul"
            sx={{ m: 0, pl: 2.5, maxHeight: 320, overflowY: "auto" }}
          >
            {duplicateLocations?.map((fl) => (
              <li key={fl}>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  {fl}
                </Typography>
              </li>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateLocations(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={spofResult !== null}
        onClose={() => setSpofResult(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>SPOF Register Imported</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {spofResult?.spof_count} SPOF
            {spofResult?.spof_count === 1 ? "" : "s"} and{" "}
            {spofResult?.mitigation_count} mitigation
            {spofResult?.mitigation_count === 1 ? "" : "s"} imported,{" "}
            {spofResult?.matched_kks_count} KKS occurrence
            {spofResult?.matched_kks_count === 1 ? "" : "s"} linked.
          </Typography>
          {spofResult && spofResult.unmatched.length > 0 && (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1.5 }}
            >
              <WarningAmberIcon sx={{ fontSize: 16, color: "#FF9800" }} />
              <Typography variant="body2" sx={{ color: "#FF9800" }}>
                {spofResult.unmatched.length} SPOF
                {spofResult.unmatched.length === 1 ? " was" : "s were"} skipped:
                no KKS linked.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSpofResult(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      {spofUpload && (
        <SpofRegisterImportDialog
          plantId={spofUpload.id}
          file={spofUpload.file}
          onClose={() => setSpofUpload(null)}
          onImported={(result) => {
            setSpofUpload(null);
            setSpofResult(result);
          }}
        />
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && editDialog.id) {
            uploadFuncLocMut.mutate({ id: editDialog.id, file });
          }
          e.target.value = "";
        }}
      />
      <input
        ref={spofInputRef}
        type="file"
        accept=".xlsx,.xlsm"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && spofPlantId.current) {
            setSpofUpload({ id: spofPlantId.current, file });
          }
          e.target.value = "";
        }}
      />
    </Box>
  );
}
